import React, { useState, useCallback, useEffect } from 'react';
import Multiselect from 'multiselect-react-dropdown';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import { AddVendor } from './AddVendor';
import {
  createVendor,
  getStateAndCity,
  getCompanyAddresses
} from '../../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';

const RFQForm = ({
  transformedVendors,
  selectedVendors,
  handleVendorSelect,
  selectedItemsForRFQ,
  itemQuantities,
  handleQuantityChange,
  onSubmit,
  onCancel,
  isSubmitDisabled,
  isSubmitting,
  refetchVendors,
  stockCriticalityFilter,
  CompanyAddress
}) => {
  const [paymentTerms, setPaymentTerms] = useState([
    {
      name: '',
      percentage: '',
      duration: ''
    }
  ]);

  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [selectedTermToAdd, setSelectedTermToAdd] = useState('');
  const [customTermText, setCustomTermText] = useState('');

  const [deliveryDate, setDeliveryDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  // Address state management
  const [addressType, setAddressType] = useState('Company'); // Company, Saved, Custom
  const [addressData, setAddressData] = useState({
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'India',
    address_id: null
  });
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);

  // Add Vendor modal state
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [isSubmittingVendor, setIsSubmittingVendor] = useState(false);

  // Mutation for creating vendor
  const { mutate: handleCreateVendor } = useMutation({
    mutationFn: createVendor,
    onMutate: () => {
      setIsSubmittingVendor(true);
    },
    onSuccess: async (data) => {
      setIsSubmittingVendor(false);
      toast.success('Vendor created successfully');
      setShowAddVendorModal(false);
      // Refetch vendors to update the list
      if (refetchVendors) {
        await refetchVendors();
      }
    },
    onError: (err) => {
      setIsSubmittingVendor(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating vendor');
    }
  });

  // Query for saved addresses
  const { data: savedAddressesData, refetch: refetchSavedAddresses } = useQuery({
    queryKey: ['savedAddresses', CompanyAddress?.id],
    queryFn: () => getCompanyAddresses(CompanyAddress?.id),
    enabled: !!CompanyAddress?.id && addressType === 'Saved'
  });

  // Handle pincode change for auto-fill
  const handlePincodeChange = useCallback(async (pincode) => {
    const cleanedPincode = pincode.replace(/[^0-9]/g, '');
    setAddressData((prev) => ({ ...prev, zip_code: cleanedPincode }));

    if (cleanedPincode.length === 6) {
      try {
        const pincodeList = await getStateAndCity(cleanedPincode);
        const postOfficeList = pincodeList[0]?.PostOffice || [];

        if (postOfficeList.length > 0) {
          const uniqueCities = [...new Set(postOfficeList.map((office) => office.Name))];
          setCityDropdownOptions(uniqueCities.map((city) => ({ value: city, label: city })));

          const stateFromPincode = postOfficeList[0].State;
          setAddressData((prev) => ({
            ...prev,
            state: stateFromPincode,
            city: uniqueCities[0] || ''
          }));
        } else {
          setCityDropdownOptions([]);
          setAddressData((prev) => ({
            ...prev,
            state: '',
            city: ''
          }));
        }
      } catch (error) {
        toast.error('The entered pincode is invalid. Please enter a valid pincode.');
        setCityDropdownOptions([]);
        setAddressData((prev) => ({
          ...prev,
          state: '',
          city: ''
        }));
      }
    }
  }, []);

  const handleOpenAddVendorModal = () => {
    setShowAddVendorModal(true);
  };

  // Handle address type change
  useEffect(() => {
    if (addressType === 'Company' && CompanyAddress) {
      setAddressData({
        address: CompanyAddress.address || '',
        city: CompanyAddress.city || '',
        state: CompanyAddress.state || '',
        zip_code: CompanyAddress.zip_code || '',
        country: CompanyAddress.country || 'India',
        address_id: null
      });
    } else if (addressType === 'Custom') {
      resetAddressFields();
    } else if (addressType === 'Saved') {
      resetAddressFields(); // Reset fields when switching to Saved
    }
  }, [addressType, CompanyAddress]);

  // Handle saved addresses data
  useEffect(() => {
    if (savedAddressesData && savedAddressesData.length > 0) {
      setSavedAddresses(savedAddressesData);
    }
  }, [savedAddressesData]);

  const paymentTermOptions = [
    { label: 'Advance', value: 'Advance' },
    { label: 'Post P.O', value: 'Post P.O' },
    { label: 'Post Delivery', value: 'Post Delivery' },
    { label: 'Other', value: 'Other' }
  ];

  const defaultTermsOptions = [
    {
      label:
        'Quotation Validity: Quotation must be valid for at least 60 days from the submission date.',
      value: 'quotation_validity'
    },
    {
      label:
        'Scope & Specifications: Vendor must quote strictly as per the drawings, BOQ, and technical specifications provided.',
      value: 'scope_specifications'
    },
    {
      label:
        'Delivery/Execution Timeline: Work/materials must be delivered within the agreed project schedule. Delays may attract penalties. Retention may apply.',
      value: 'delivery_timeline'
    },
    {
      label:
        'Compliance & Safety: Vendor must comply with all local laws, safety standards, and environmental regulations.',
      value: 'compliance_safety'
    },
    { label: 'Other', value: 'other' }
  ];

  const handlePaymentTermChange = (index, field, value) => {
    const updatedTerms = [...paymentTerms];
    updatedTerms[index][field] = value;
    setPaymentTerms(updatedTerms);
  };

  const addPaymentTerm = () => {
    setPaymentTerms([
      ...paymentTerms,
      {
        name: '',
        percentage: '',
        duration: ''
      }
    ]);
  };

  const removePaymentTerm = (index) => {
    if (paymentTerms.length > 1) {
      const updatedTerms = paymentTerms.filter((_, i) => i !== index);
      setPaymentTerms(updatedTerms);
    }
  };

  const addTerm = () => {
    if (selectedTermToAdd === 'other') {
      if (!customTermText.trim()) {
        toast.error('Please enter a custom term');
        return;
      }
      setTermsAndConditions([...termsAndConditions, customTermText]);
      setCustomTermText('');
    } else if (selectedTermToAdd) {
      const selectedOption = defaultTermsOptions.find(
        (option) => option.value === selectedTermToAdd
      );
      if (selectedOption) {
        setTermsAndConditions([...termsAndConditions, selectedOption.label]);
      }
    } else {
      toast.error('Please select a term to add');
      return;
    }
    setSelectedTermToAdd('');
  };

  const removeTerm = (index) => {
    const updatedTerms = termsAndConditions.filter((_, i) => i !== index);
    setTermsAndConditions(updatedTerms);
  };

  // Handle saved address selection
  const handleSavedAddressSelect = (addressId) => {
    const selectedAddress = savedAddresses.find((addr) => addr.id === addressId);
    if (selectedAddress) {
      setAddressData({
        address: selectedAddress.street || '', // street instead of address
        city: selectedAddress.city || '',
        state: selectedAddress.state || '',
        zip_code: selectedAddress.zipCode || '', // zipCode instead of zip_code
        country: selectedAddress.country || 'India',
        address_id: selectedAddress.id
      });
    }
  };

  // Handle address field changes
  const handleAddressFieldChange = (field, value) => {
    setAddressData((prev) => ({ ...prev, [field]: value }));
  };

  // Reset address fields
  const resetAddressFields = () => {
    setAddressData({
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'India',
      address_id: null
    });
    setCityDropdownOptions([]);
  };

  const handleSubmit = () => {
    // Validate payment terms
    for (const term of paymentTerms) {
      if (term.name && term.percentage) {
        // Skip duration validation for advance payments
        if (term.name !== 'Advance' && !term.duration) {
          toast.error('Please enter duration for payment terms other than advance');
          return;
        }

        if (term.name === 'Other' && !term.customName) {
          toast.error('Please enter a custom payment term name');
          return;
        }
      }
    }

    // Filter out empty payment terms
    const validPaymentTerms = paymentTerms
      .filter((term) => term.name && term.percentage && (term.name === 'Advance' || term.duration))
      .map((term) => ({
        name: term.name === 'Other' ? term.customName : term.name,
        percentage: parseFloat(term.percentage),
        duration: term.name === 'Advance' ? 0 : parseInt(term.duration)
      }));

    // Filter out empty terms and conditions
    const validTermsAndConditions = termsAndConditions.filter((term) => term.trim() !== '');

    // Validate delivery date
    if (!deliveryDate) {
      toast.error('Please select a delivery date');
      return;
    }

    // Validate expiry date
    if (!expiryDate) {
      toast.error('Please select an expiry date');
      return;
    }

    // Validate terms and conditions
    if (validTermsAndConditions.length === 0) {
      toast.error('Please add at least one term and condition');
      return;
    }

    // Validate address
    if (!addressData.address || !addressData.city || !addressData.state || !addressData.zip_code) {
      toast.error('Please fill all address fields');
      return;
    }

    const payload = {
      vendor_ids: selectedVendors,
      paymentTerms: validPaymentTerms,
      termsAndCondition: validTermsAndConditions,
      deliveryDate: deliveryDate,
      expiryDate: expiryDate,
      addressType: addressType,
      address: addressData.address,
      city: addressData.city,
      state: addressData.state,
      zip_code: addressData.zip_code,
      country: addressData.country,
      address_id: addressData.address_id,
      items: selectedItemsForRFQ?.map((item) => ({
        id: item.id,
        quantity: parseInt(itemQuantities[item.id] || 0)
      }))
    };

    onSubmit(payload);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[28rem]">
        {/* Left Side - Existing Content */}
        <div className="space-y-6 overflow-y-auto pr-2">
          {/* Vendor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Vendors *</label>
            {transformedVendors.length > 0 ? (
              <Multiselect
                options={transformedVendors}
                selectedValues={transformedVendors.filter((vendor) =>
                  selectedVendors.includes(vendor.id)
                )}
                onSelect={handleVendorSelect}
                onRemove={handleVendorSelect}
                displayValue="name"
                placeholder="Select vendors..."
                showCheckbox
                style={{
                  optionContainer: {
                    border: '1px solid',
                    backgroundColor: 'white'
                  },
                  option: {
                    color: 'black',
                    backgroundColor: 'white'
                  },
                  chips: {
                    background: '#2A565E',
                    color: 'white'
                  }
                }}
              />
            ) : (
              <div className="p-3 border border-gray-300 rounded-md bg-gray-50">
                <p className="text-sm text-gray-600">
                  No vendors available for the selected items&apos; categories. Please select
                  different items or contact support to add vendors for these categories.
                </p>
              </div>
            )}
          </div>

          {/* Items List */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-md">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      SKU
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Item Name (Unit)
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Required Stock (in {stockCriticalityFilter} days)
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Stock to be Ordered
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      Enter Qty *
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedItemsForRFQ?.map((item) => {
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-600 border-b">
                          {item.id || '0'}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 border-b">
                          {item.item_name || '---'}
                          {item.unit && <span className="text-gray-500"> ({item.unit})</span>}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 border-b">
                          {item?.requiredStock}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 border-b">
                          {item?.stock_to_be_ordered > 0 ? item?.stock_to_be_ordered : 'In Stock'}
                        </td>
                        <td className="px-3 py-3 text-sm border-b">
                          <input
                            type="number"
                            min="1"
                            value={itemQuantities[item.id] || ''}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            onFocus={(e) => {
                              // Auto-fill with stock to order if greater than 0 and field is empty
                              if (!e.target.value && item?.stock_to_be_ordered > 0) {
                                handleQuantityChange(item.id, item?.stock_to_be_ordered.toString());
                              }
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder={
                              item?.stock_to_be_ordered > 0
                                ? item?.stock_to_be_ordered.toString()
                                : 'Enter quantity'
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Side - New Content */}
        <div className="space-y-6 overflow-y-auto pr-2">
          {/* Payment Terms */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Payment Terms *</label>
              <Button
                type="button"
                displayType="secondary"
                onClick={addPaymentTerm}
                customStyle={{
                  height: '2rem',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem'
                }}>
                + Add Term
              </Button>
            </div>
            <div className="space-y-3">
              {paymentTerms.map((term, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Term {index + 1}</span>
                    {paymentTerms.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePaymentTerm(index)}
                        className="text-red-500 hover:text-red-700 text-sm">
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Payment Type *
                      </label>
                      <select
                        value={term.name}
                        onChange={(e) => handlePaymentTermChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                        <option value="">Select payment type</option>
                        {paymentTermOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {term.name === 'Other' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Custom Payment Term *
                        </label>
                        <input
                          type="text"
                          placeholder="Enter custom payment term"
                          value={term.customName || ''}
                          onChange={(e) =>
                            handlePaymentTermChange(index, 'customName', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Amount (%) *
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={term.percentage}
                          onChange={(e) =>
                            handlePaymentTermChange(index, 'percentage', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      {term.name !== 'Advance' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Duration (days) *
                          </label>
                          <input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={term.duration}
                            onChange={(e) =>
                              handlePaymentTermChange(index, 'duration', e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Terms and Conditions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Terms and Conditions *
            </label>

            {/* Term Selection and Add */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Select Term to Add
                </label>
                <select
                  value={selectedTermToAdd}
                  onChange={(e) => setSelectedTermToAdd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">Select a term</option>
                  {defaultTermsOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {selectedTermToAdd === 'other' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Custom Term *
                  </label>
                  <textarea
                    value={customTermText}
                    onChange={(e) => setCustomTermText(e.target.value)}
                    placeholder="Enter custom terms and conditions"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                  />
                </div>
              )}

              <Button
                type="button"
                displayType="secondary"
                onClick={addTerm}
                customStyle={{
                  height: '2rem',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem'
                }}>
                + Add Selected Term
              </Button>
            </div>

            {/* Added Terms List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {termsAndConditions.map((term, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-700">Term {index + 1}:</span>
                      <p className="text-sm text-gray-600 mt-1">{term}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeTerm(index)}
                      className="text-red-500 hover:text-red-700 text-sm ml-2">
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Date and Expiry Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Date *
              </label>
              <input
                type="date"
                value={deliveryDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  if (selectedDate < today) {
                    toast.error('Delivery date cannot be in the past');
                    return;
                  }
                  setDeliveryDate(e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date *</label>
              <input
                type="date"
                value={expiryDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  if (selectedDate < today) {
                    toast.error('Expiry date cannot be in the past');
                    return;
                  }
                  setExpiryDate(e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Address Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Delivery Address *
            </label>

            {/* Address Type Radio Buttons */}
            <div className="flex gap-4 mb-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="addressType"
                  value="Company"
                  checked={addressType === 'Company'}
                  onChange={(e) => setAddressType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Company Address</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="addressType"
                  value="Saved"
                  checked={addressType === 'Saved'}
                  onChange={(e) => setAddressType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Saved Address</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="addressType"
                  value="Custom"
                  checked={addressType === 'Custom'}
                  onChange={(e) => setAddressType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Custom Address</span>
              </label>
            </div>

            {/* Saved Address Dropdown */}
            {addressType === 'Saved' && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Select Saved Address
                </label>
                <select
                  value={addressData.address_id || ''}
                  onChange={(e) => handleSavedAddressSelect(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">Select a saved address</option>
                  {savedAddresses.map((address) => (
                    <option key={address.id} value={address.id}>
                      {address.street} - {address.city}, {address.state}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Address Form Fields */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Street Address *
                </label>
                <input
                  type="text"
                  value={addressData.address}
                  onChange={(e) => handleAddressFieldChange('address', e.target.value)}
                  disabled={addressType === 'Company' || addressType === 'Saved'}
                  placeholder="Enter complete street address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Zip/Pin Code *
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={addressData.zip_code}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    disabled={addressType === 'Company' || addressType === 'Saved'}
                    placeholder="Enter pincode"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">State *</label>
                  <input
                    type="text"
                    value={addressData.state}
                    onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                    disabled={addressType === 'Company' || addressType === 'Saved'}
                    placeholder="State"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City *</label>
                  {cityDropdownOptions.length > 0 ? (
                    <select
                      value={addressData.city}
                      onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                      disabled={addressType === 'Company' || addressType === 'Saved'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100">
                      <option value="">Select city</option>
                      {cityDropdownOptions.map((city) => (
                        <option key={city.value} value={city.value}>
                          {city.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={addressData.city}
                      onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                      disabled={addressType === 'Company' || addressType === 'Saved'}
                      placeholder="City"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Country *</label>
                  <input
                    type="text"
                    value={addressData.country}
                    onChange={(e) => handleAddressFieldChange('country', e.target.value)}
                    disabled={addressType === 'Company' || addressType === 'Saved'}
                    placeholder="Country"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={handleOpenAddVendorModal}
          className="text-blue-500 hover:text-blue-700 text-sm font-semibold underline cursor-pointer">
          Add New Vendor
        </button>
        <div className="flex gap-3">
          <Button
            customStyle={{
              height: '2.5rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#6B7280',
              color: 'white',
              border: 'none'
            }}
            type="button"
            onClick={onCancel}>
            Cancel
          </Button>
          <Button
            customStyle={{
              height: '2.5rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: 'none'
            }}
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled() || isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit RFQ'}
          </Button>
        </div>
      </div>

      {/* Add Vendor Modal */}
      {showAddVendorModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name="Add Vendor"
          onClose={() => setShowAddVendorModal(false)}>
          <AddVendor
            isSubmitting={isSubmittingVendor}
            modalType="Add"
            onSubmit={handleCreateVendor}
            onClose={() => setShowAddVendorModal(false)}
            selectedVendor={null}
            setModalType={() => {}}
            refetchVendors={refetchVendors || (() => {})}
            setOpenAddVendor={setShowAddVendorModal}
            OpenAddVendor={showAddVendorModal}
          />
        </Modal>
      )}
    </div>
  );
};

export default RFQForm;
